import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDedupScan, useDedupMerge } from '../api/intelligence';

const TYPE_BADGES: Record<string, string> = {
  person: 'bg-blue-500/20 text-blue-400',
  organization: 'bg-purple-500/20 text-purple-400',
  location: 'bg-green-500/20 text-green-400',
};

export default function DeduplicationPage() {
  const navigate = useNavigate();
  const [entityType, setEntityType] = useState('');
  const [minSimilarity, setMinSimilarity] = useState(0.7);
  const scanMutation = useDedupScan(entityType || undefined, minSimilarity);
  const mergeMutation = useDedupMerge();

  const handleScan = () => scanMutation.mutate();

  const handleMerge = async (primaryId: string, secondaryId: string) => {
    await mergeMutation.mutateAsync({ primary_id: primaryId, secondary_id: secondaryId });
    scanMutation.mutate(); // rescan after merge
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-gray-100">Deduplication</h1>
        <p className="text-sm text-gray-500 mt-1">Detection et fusion des entites en double</p>
      </div>

      {/* Controls */}
      <div className="glass-panel p-5 flex items-end gap-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Type d'entite</label>
          <select
            value={entityType}
            onChange={(e) => setEntityType(e.target.value)}
            className="px-3 py-2 text-sm bg-surface-tertiary border border-border-subtle rounded-lg text-gray-300"
          >
            <option value="">Tous</option>
            <option value="person">Personnes</option>
            <option value="organization">Organisations</option>
            <option value="location">Lieux</option>
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">Similarite min: {Math.round(minSimilarity * 100)}%</label>
          <input
            type="range"
            min={0.5}
            max={1}
            step={0.05}
            value={minSimilarity}
            onChange={(e) => setMinSimilarity(Number(e.target.value))}
            className="w-40"
          />
        </div>
        <button
          onClick={handleScan}
          disabled={scanMutation.isPending}
          className="px-5 py-2 text-sm font-medium bg-cyber-cyan/10 text-cyber-cyan rounded-lg hover:bg-cyber-cyan/20 disabled:opacity-50 transition-colors"
        >
          {scanMutation.isPending ? 'Scan en cours...' : 'Lancer le scan'}
        </button>
      </div>

      {/* Results */}
      {scanMutation.data && (
        <div className="space-y-3">
          <p className="text-sm text-gray-400">
            {scanMutation.data.duplicates.length} doublons potentiels trouves sur {scanMutation.data.total_scanned} entites
          </p>

          {scanMutation.data.duplicates.map((dup, i) => (
            <div key={i} className="glass-panel p-4 flex items-center gap-4">
              {/* Entity A */}
              <button
                onClick={() => navigate(`/entities/${dup.entity_a.id}`)}
                className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg bg-surface-tertiary/50 hover:bg-surface-tertiary transition-colors"
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold ${TYPE_BADGES[dup.entity_a.entity_type] || 'bg-gray-500/20 text-gray-400'}`}>
                  {dup.entity_a.name.charAt(0).toUpperCase()}
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-200">{dup.entity_a.name}</p>
                  <p className="text-xs text-gray-500">{dup.entity_a.entity_type}</p>
                </div>
              </button>

              {/* Similarity */}
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <span className={`text-lg font-bold ${dup.similarity >= 0.9 ? 'text-red-400' : dup.similarity >= 0.8 ? 'text-amber-400' : 'text-green-400'}`}>
                  {Math.round(dup.similarity * 100)}%
                </span>
                <span className="text-xs text-gray-500">similarite</span>
                <div className="flex gap-1 flex-wrap justify-center">
                  {dup.matching_fields.map((f) => (
                    <span key={f} className="px-1.5 py-0.5 text-xs bg-surface-tertiary text-gray-400 rounded">{f}</span>
                  ))}
                </div>
              </div>

              {/* Entity B */}
              <button
                onClick={() => navigate(`/entities/${dup.entity_b.id}`)}
                className="flex-1 flex items-center gap-3 px-3 py-2 rounded-lg bg-surface-tertiary/50 hover:bg-surface-tertiary transition-colors"
              >
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold ${TYPE_BADGES[dup.entity_b.entity_type] || 'bg-gray-500/20 text-gray-400'}`}>
                  {dup.entity_b.name.charAt(0).toUpperCase()}
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-gray-200">{dup.entity_b.name}</p>
                  <p className="text-xs text-gray-500">{dup.entity_b.entity_type}</p>
                </div>
              </button>

              {/* Merge button */}
              <button
                onClick={() => handleMerge(dup.entity_a.id, dup.entity_b.id)}
                disabled={mergeMutation.isPending}
                className="px-3 py-2 text-xs font-medium bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 disabled:opacity-50 flex-shrink-0"
              >
                Fusionner
              </button>
            </div>
          ))}

          {scanMutation.data.duplicates.length === 0 && (
            <p className="text-center text-gray-500 py-8">Aucun doublon detecte</p>
          )}
        </div>
      )}
    </div>
  );
}
